import moment from 'moment';
import {
  buildImageSyncFormData as buildEntityImageSyncFormData,
  IMAGE_ENTITY_TYPE,
  mapEntityImages
} from '../../../../../components/EntityImagesGallery';
import { amenityDefaultValues, createEmptyDailySchedule } from './amenityFormSchemaV2';
import { buildPaymentFieldsFromApi } from './amenityPaymentConfigV2';
import {
  normalizeSlotDurationMinutes,
  snapAutoBlockDuration
} from './addAmenityScheduleHelpersV2';

const DAY_OF_WEEK_TO_CODE = {
  1: 'M',
  2: 'T',
  3: 'W',
  4: 'TH',
  5: 'F',
  6: 'S',
  7: 'SU'
};

function mapBuildingIds(buildingIds, allBuildings) {
  if (!Array.isArray(buildingIds) || buildingIds.length === 0) return [];
  return buildingIds.map((rawId) => {
    const id = Number(rawId);
    const found = (allBuildings || []).find((b) => Number(b.id) === id);
    return {
      value: id,
      label: found?.name ?? String(rawId)
    };
  });
}

export function resolveAmenityPrimaryBuilding(apiData, allBuildings = []) {
  const primaryId = apiData?.building_id;
  if (primaryId == null || primaryId === '') return null;

  const id = Number(primaryId);
  if (!Number.isFinite(id)) return null;

  const found = (allBuildings || []).find((b) => Number(b.id) === id);
  if (found) return found;

  const name = apiData?.building_name ?? apiData?.building;
  return {
    id,
    name: name != null && String(name).trim() !== '' ? String(name) : String(id)
  };
}

function utcScheduleToFractionalHours(startIso, endIso) {
  if (startIso === endIso) {
    return { start: 0, end: 24 };
  }

  const startVal = moment.utc(startIso);
  const endVal = moment.utc(endIso);
  let start = startVal.hours() + startVal.minutes() / 60;
  let end = endVal.hours() + endVal.minutes() / 60;

  if (
    endVal.isAfter(startVal, 'day') ||
    end <= start ||
    (endVal.hours() === 23 && endVal.minutes() >= 59)
  ) {
    end = 24;
  }

  return { start, end };
}

function mapSchedules(apiSchedules, dayOptions) {
  const selectedDays = [];
  const daily_schedule = createEmptyDailySchedule();
  const dayOptionByCode = (dayOptions || []).reduce((acc, opt) => {
    if (opt?.value != null) acc[opt.value] = opt;
    return acc;
  }, {});

  (apiSchedules || []).forEach((s) => {
    const code = DAY_OF_WEEK_TO_CODE[Number(s.day_of_week)];
    if (!code) return;

    if (s.is_closed) {
      daily_schedule[code] = { start: null, end: null };
      return;
    }

    const dayOpt = dayOptionByCode[code];
    if (dayOpt && !selectedDays.some((d) => d.value === code)) {
      selectedDays.push(dayOpt);
    }

    const { start, end } = utcScheduleToFractionalHours(s.start_time, s.end_time);
    daily_schedule[code] = { start, end };
  });

  return { selectedDays, daily_schedule };
}

export function extractExistingScheduleIds(apiSchedules) {
  return (apiSchedules || [])
    .filter((s) => s?.id != null && s?.day_of_week != null)
    .map((s) => ({
      id: Number(s.id),
      day_of_week: Number(s.day_of_week)
    }));
}

function mapAmenityImages(images) {
  return mapEntityImages(images);
}

export function mapBackendToFormValues(apiData, dayOptions, allBuildings = []) {
  const allowMultiDay = !!apiData?.allow_multi_day_bookings;
  const slotMinutes = normalizeSlotDurationMinutes(apiData?.slot_duration);
  const { selectedDays, daily_schedule } = mapSchedules(
    apiData?.amenities_schedules,
    dayOptions
  );

  const formValues = {
    ...amenityDefaultValues,
    type_id: apiData?.type_id != null ? String(apiData.type_id) : '',
    code: apiData?.code ?? '',
    rich_description: apiData?.rich_description ?? '',
    slot_duration_minutes: slotMinutes,
    allow_booking: !!apiData?.allow_booking,
    require_reservation_form: !!apiData?.require_reservation_form,
    require_payment: !!apiData?.require_payment,
    ...buildPaymentFieldsFromApi(apiData),
    require_manager_approval: !!apiData?.require_manager_approval,
    enable_post_booking_auto_block: !!apiData?.enable_post_booking_auto_block,
    post_booking_auto_block_duration: snapAutoBlockDuration(
      apiData?.post_booking_auto_block_duration,
      slotMinutes
    ),
    post_block_description:
      apiData?.post_block_description != null ? String(apiData.post_block_description) : '',
    allow_multi_day: allowMultiDay,
    allow_same_day_multiple_bookings: !!apiData?.allow_multiple_bookings_per_day,
    booking_limits_unit:
      apiData?.booking_limit_unit != null ? Number(apiData.booking_limit_unit) : 0,
    max_advance_booking:
      apiData?.advance_booking_limit_days != null
        ? Number(apiData.advance_booking_limit_days)
        : amenityDefaultValues.max_advance_booking,
    building_ids: mapBuildingIds(apiData?.building_ids, allBuildings),
    selectedDays,
    daily_schedule,
    data: apiData?.data && typeof apiData.data === 'object' ? apiData.data : {}
  };

  if (allowMultiDay) {
    formValues.min_duration_value =
      apiData?.min_time != null ? Number(apiData.min_time) : null;
    formValues.max_duration_value =
      apiData?.max_time != null ? Number(apiData.max_time) : null;
    formValues.min_reservation_hours = 0;
    formValues.max_reservation_hours = null;
  } else {
    formValues.min_reservation_hours =
      apiData?.min_time != null ? Number(apiData.min_time) : 0;
    formValues.max_reservation_hours =
      apiData?.max_time != null ? Number(apiData.max_time) : null;
    formValues.min_duration_value = null;
    formValues.max_duration_value = null;
  }

  const waiverHydration =
    apiData?.require_reservation_form && apiData?.waiver_form
      ? {
          id: apiData.waiver_form.id,
          name: apiData.waiver_form.name,
          url: apiData.waiver_form.url,
          isExisting: true
        }
      : null;

  formValues.waiver_upload = waiverHydration;

  const localState = {
    amenityImages: mapAmenityImages(apiData?.images),
    existingSchedules: extractExistingScheduleIds(apiData?.amenities_schedules),
    waiverFile: waiverHydration
  };

  return { formValues, localState };
}

export function buildImageSyncFormData({
  amenityImages,
  amenityId,
  orgId,
  buildingId
}) {
  return buildEntityImageSyncFormData({
    images: amenityImages,
    entityId: amenityId,
    entityType: IMAGE_ENTITY_TYPE.AMENITY,
    orgId,
    buildingId
  });
}
